import { useState, useEffect } from 'react'
import { CallMeWidget } from '@vp/care-react-c2c'
import styles, { stylesheet } from './CallMe.scss'
import CallMeService from '../../services/callMe/callMeService'
import { withTranslation } from 'react-i18next'
import { Typography, Button, Row, Column, Spinner, Callout, FlexBox, Icon } from '@vp/swan'
import callMeHelper from '../../helpers/callMeHelper'
import { CallMeButton } from './CallMeButton'
import DisabledCallMe from './DisabledCallMe'
import { useBannerStatusContext } from '../../contexts/StatusContext'
import useServiceChecker from '../../hooks/useServiceChecker'
import useStatusChecker from '../../hooks/useStatusChecker'
import { useStyles } from '@vp/ubik-context'
import { useConfig } from '../../contexts/ConfigProvider'
// This const contains locales that shouldn't have the timezone displayed
const SINGLE_TIME_ZONE_LOCALES = ['en-gb', 'en-ie']

const CallMe = (props) => {
  const { t, fallBack } = props
  const lng = props.locale.toLowerCase().replace('-', '_')
  const [callMeOpen, setCallMeOpen] = useState(false)
  const [callMeDisabled, setCallMeDisabled] = useState(false)
  const [callMeResponse, setCallMeResponse] = useState({})
  const [formattedSchedule, setFormattedSchedule] = useState([])
  const [businessHoursTimezone, setBusinessHoursTimezone] = useState('')
  const { available, loading } = useServiceChecker()
  const { initialData } = useBannerStatusContext()
  const { data: status, isLoading: loadingStatus } = useStatusChecker(props.tenant, props.locale, initialData)
  const config = useConfig()
  const callMeService = new CallMeService()
  useStyles(stylesheet)
  useEffect(() => {
    async function fetchCallMeService() {
      const response = await callMeService.getCallMeSchedule(props.tenant, props.locale, props.segment, config)
      if (response?.status && response?.businessHours && response?.businessHours?.timezone?.short_format) {
        setCallMeOpen(response.status.isScheduleOpen)
        setCallMeDisabled(!response.status.isFeatureEnabled)
        setBusinessHoursTimezone(`${response.businessHours.timezone.short_format}`)
        setFormattedSchedule(callMeHelper.formatSchedule(response.businessHours))
        setCallMeResponse(response)
      }
    }

    fetchCallMeService()
  }, [])

  useEffect(() => {
    if (callMeResponse?.status && callMeResponse?.businessHours) {
      if (callMeResponse?.status?.TTL > 0 && callMeResponse?.status?.remainingTime > 0) {
        setTimeout(
          () => {
            // if this.state.remainingTime<this.state.ttl => not a recurrent update
            callMeHelper.updateCallMe(
              callMeResponse,

              callMeService,
              callMeResponse.status.TTL < callMeResponse.status.remainingTime,
              this,
            )
            setInterval(
              callMeHelper.updateCallMe,
              Math.min(callMeResponse.status.remainingTime, callMeResponse.status.TTL) * 1000,
              callMeResponse,

              callMeService,
              callMeResponse.status.TTL < callMeResponse.status.remainingTime,
              this,
            )
          },
          Math.min(callMeResponse.status.remainingTime, callMeResponse.status.TTL) * 1000,
        ) /// change this to always be the min between
      }
    }
    return () => {
      clearInterval(0)
      clearTimeout(0)
    }
  }, [callMeResponse])

  if (loading || loadingStatus) {
    return (
      <Spinner
        mt={{ lg: '7', md: '7', sm: '6', xs: '6' }}
        style={{ width: '100%' }}
        accessibleText={t(`${props.tenant}:search_results.preloader_accessibility_label`, { lng })}
        size="standard"
      />
    )
  }

  if (!available && !status?.enabled) return fallBack

  if (status?.enabled && status?.config?.c2cDisabled)
    return <DisabledCallMe {...props} callMeOpen={callMeOpen} callMeResponse={callMeResponse} callMeDisabled={callMeDisabled} />

  return (
    <Column className={styles.callmeColumnContainer} span={12}>
      <Row style={{ justifyContent: 'space-between' }}>
        <Typography fontSkin="title-subsection">{t(`${props.tenant}:call_me.title`, { lng })}</Typography>
        <Callout
          style={{
            display: callMeResponse?.status && callMeResponse?.businessHours && !callMeDisabled && callMeOpen ? 'inline-flex' : 'none',
          }}
          variant="inverse"
        >
          {t(`${props.tenant}:call_me.badge`, { lng })}
        </Callout>
      </Row>
      {!(callMeResponse?.status && callMeResponse?.businessHours) && (
        <Row pt={{ lg: '7', md: '7', sm: '6', xs: '6' }}>
          <div id="call_me_preloader">
            <Spinner
              mt={{ lg: '7', md: '7', sm: '6', xs: '6' }}
              accessibleText={t(`${props.tenant}:search_results.preloader_accessibility_label`, {
                lng,
              })}
              size="standard"
            />
          </div>
        </Row>
      )}
      <Row pt="5">
        <div id="callMeDescriptionDiv" style={{ width: '100%' }}>
          <Typography
            component="p"
            fontSize="standard"
            id="call_me_description_offline"
            style={{
              display: callMeResponse?.status && callMeResponse?.businessHours && !callMeOpen ? 'block' : 'none',
            }}
          >
            {t(`${props.tenant}:call_me.buttons.call_me_offline.description`, {
              lng,
            })}{' '}
          </Typography>
          <Typography
            component="p"
            fontSize="standard"
            id="call_me_description_unavailable"
            style={{
              display: callMeResponse?.status && callMeResponse?.businessHours && callMeDisabled ? 'block' : 'none',
            }}
          >
            {t(`${props.tenant}:call_me.service_unavailable`, { lng })}{' '}
          </Typography>
          <div
            id="call_me_business_hours_online"
            style={{
              display: callMeResponse?.status && callMeResponse?.businessHours ? 'grid' : 'none',
            }}
          >
            {formattedSchedule?.length > 0 &&
              formattedSchedule.map((scheduleEntry, i) => (
                <div style={{ display: 'block' }} key={`${scheduleEntry.startTime}–${scheduleEntry.endTime}-${i}`}>
                  <div style={{ width: 'fit-content', float: 'left' }}>
                    {' '}
                    {scheduleEntry.startDay}
                    {scheduleEntry.startDay !== scheduleEntry.endDay ? <span> &ndash; {scheduleEntry.endDay}</span> : null}
                  </div>
                  <div style={{ width: 'fit-content', float: 'right' }}>
                    {scheduleEntry.startTime} – {scheduleEntry.endTime}{' '}
                    {!SINGLE_TIME_ZONE_LOCALES.includes(props.locale.toLowerCase()) && <span>{businessHoursTimezone}</span>}
                  </div>
                </div>
              ))}
          </div>
          <Typography
            component="p"
            fontSize="standard"
            id="call_me_description_online"
            style={{
              display: callMeResponse?.status && callMeResponse?.businessHours && !callMeDisabled && callMeOpen ? 'block' : 'none',
            }}
          >
            {t(`${props.tenant}:call_me.buttons.call_me_online.description`, {
              lng,
            })}
          </Typography>
        </div>
      </Row>
      <Row pt="5">
        <div
          id="callMeButtonDiv"
          style={{
            display: !callMeDisabled ? 'block' : 'none',
            width: '100%',
          }}
        >
          <CallMeWidget
            tenant={props.tenant}
            locale={props.locale}
            pageName="Home Page:Help Center"
            pageStage="Customer Care"
            pageSection="Help Center"
            origin="Help-Center-C2C"
          >
            {callMeResponse?.status && callMeResponse?.businessHours && callMeOpen && !callMeDisabled ? (
              <CallMeButton tenant={props.tenant} t={t} lng={lng} locale={props.locale} />
            ) : null}
          </CallMeWidget>
          <Button
            disabled="disabled"
            id="call_me_button_offline"
            style={{
              display: callMeResponse?.status && callMeResponse?.businessHours && !callMeOpen ? 'block' : 'none',
            }}
            type="button"
            name="call_me_offline"
            value="call_me_offline"
            iconPosition="left"
          >
            <FlexBox>
              <Icon iconType="phone" mr={3} />
              {t(`${props.tenant}:call_me.buttons.call_me_offline.title`, {
                lng,
              })}
            </FlexBox>
          </Button>
        </div>
      </Row>
    </Column>
  )
}
export default withTranslation()(CallMe)
